import React, { useEffect, useRef, useState } from 'react'
import { connect, useSelector, useStore } from 'react-redux'
import { Trans } from 'react-i18next'
import { i18n } from 'shared/i18n'
import moment from 'moment-timezone'
import { NavLink } from 'react-router-dom'
import Dropdown from 'react-materialize/lib/Dropdown'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { notificationsEnabled, notificationsDisabled } from '../../reducers/user_profile'
import { getNotificationGroups } from '../../selectors/notifications'
import { decidePendingBooking } from './notificationActions'
import { ContactUs } from './ContactUs'
import { SwitchInput } from './SwitchInput'
import { NotificationsNotSupported } from '../HelpHints'
import cx from 'classnames'
import './NotificationsPanel.css'

export { getNotifications, getNotificationGroups } from '../../selectors/notifications'

const useNotifications = () => {
    const [now, setNow] = useState(Date.now)
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 30000)
        return () => clearInterval(timer)
    }, [])
    return useSelector(state => getNotificationGroups(state, now))
}

export const BookingNotification = ({ booking, item, supplItem, user, site, past, recordedDecision, decidedAt }) => {
    const store = useStore()
    const liveDecision = useSelector(state => state.ui.bookingDecisions?.[booking.id]?.decision)
    const decision = recordedDecision || liveDecision
    const busy = decision === 'accepting' || decision === 'rejecting'
    const completed = decision === 'accepted' || decision === 'rejected'
    const status = completed ? (decision === 'accepted' ? 'Accepted' : 'Rejected')
        : past ? 'Booking time has passed' : 'Awaiting confirmation'
    const clubDate = date => site.timezone ? moment.tz(date, site.timezone)
        : moment.parseZone(date + site.timezoneOffsetSuffix)

    return <Paper component="article" variant="outlined" className="notification-card"
        sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }} aria-busy={busy} tabIndex={past ? -1 : undefined}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
                <Box role="status">
                    <Chip size="small" color={decision === 'rejected' ? 'error' : completed ? 'success' : past ? 'default' : 'warning'}
                        variant="outlined" label={<Trans>{status}</Trans>}
                        sx={{ mb: 1, color: !completed && !past ? 'text.primary' : undefined }} />
                </Box>
                <Typography component="div" fontWeight={600}>
                    {user.id ? <NavLink to={`/admin/clients/id/${user.id}`}>{user.displayName}</NavLink>
                        : <Trans>{user.displayName}</Trans>}
                </Typography>
                <Typography component="div" variant="body2" sx={{ mt: 0.5 }}>
                    <Trans>{item.name}</Trans>{supplItem && <> · {supplItem.name}</>}
                </Typography>
                <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {clubDate(booking.fromDate).format('LL')} · {clubDate(booking.fromDate).format('LT')}
                    {' – '}{clubDate(booking.toDate).format('LT')}
                </Typography>
                {booking.quantity > 1 && <Typography component="div" variant="body2" color="text.secondary">
                    <Trans>Quantity</Trans>: {booking.quantity}
                </Typography>}
                {decidedAt && <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <Trans>Decision made</Trans>: { (site.timezone ? moment(decidedAt).tz(site.timezone)
                        : moment(decidedAt).utcOffset(site.timezoneOffsetSuffix)).format('LLL') }
                </Typography>}
            </Box>
            {!past && !completed && <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                <Button variant="contained" disableElevation disabled={busy} sx={{ minHeight: 44 }}
                    onClick={() => decidePendingBooking(store, booking, true)}>
                    {decision === 'accepting' && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
                    <Trans>{decision === 'accepting' ? 'Accepting…' : 'Accept'}</Trans>
                </Button>
                <Button variant="outlined" color="error" disabled={busy} sx={{ minHeight: 44 }}
                    onClick={() => decidePendingBooking(store, booking, false)}>
                    {decision === 'rejecting' && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
                    <Trans>{decision === 'rejecting' ? 'Rejecting…' : 'Reject'}</Trans>
                </Button>
            </Stack>}
        </Stack>
        {decision === 'failed' && <Alert severity="error" sx={{ mt: 2 }}>
            <Trans>Operation has failed, try again later</Trans>
        </Alert>}
    </Paper>
}

const SyncErrorNotification = ({ itemName }) => <Paper component="article" variant="outlined"
    className="notification-card" sx={{ p: 2, borderRadius: 2 }}>
    <Alert severity="error">
        <Typography component="div" fontWeight={600}><Trans>{itemName}</Trans></Typography>
        <Typography component="div" variant="body2" sx={{ my: 0.5 }}>
            <Trans>Error synchronizing with Google Calendar!</Trans>
        </Typography>
        <ContactUs />
    </Alert>
</Paper>

const renderNotification = (notification, past = false) => notification.type !== 'sync_calendar'
    ? <BookingNotification {...notification.data} past={past} />
    : <SyncErrorNotification {...notification.data} />

export const NotificationsBadge = ({ number, corner, line }) => !!number
    ? <div className={cx('notifications-badge', { 'badge-corner': corner, 'badge-line': line })}>{number}</div>
    : <div />

export const ConnectedNotificationsBadge = props => {
    const { active } = useNotifications()
    return <NotificationsBadge {...props} number={active.length} />
}

export const NotificationsIcon = () => {
    const { active } = useNotifications()
    return <Dropdown trigger={<button className="link-button nav-button" aria-label={i18n().t('Notifications')}>
        <div className="notifications-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6v-5c0-3.1-1.6-5.6-4.5-6.3V4a1.5 1.5 0 0 0-3 0v.7C7.6 5.4 6 7.9 6 11v5l-2 2v1h16v-1z" />
            </svg>
            <NotificationsBadge number={active.length} corner />
        </div>
    </button>} options={{ belowOrigin: true, closeOnClick: false, stoppropagation: true }}>
        {active.map(notification => <li key={notification.id} className="notifications-dropdown-entry">
            {renderNotification(notification)}
        </li>)}
        <li><div className="see-all"><NavLink to="/notifications"><Trans>See all</Trans></NavLink></div></li>
    </Dropdown>
}

const PAST_PAGE_SIZE = 20

const NotificationSection = ({ notifications, past }) => {
    const [visibleCount, setVisibleCount] = useState(PAST_PAGE_SIZE)
    const listRef = useRef(null)
    const visible = past ? notifications.slice(0, visibleCount) : notifications
    const title = past ? 'Past notifications' : 'Active notifications'
    const headingId = past ? 'past-notifications-heading' : 'active-notifications-heading'
    return <Box component="section" aria-labelledby={headingId} sx={{ mt: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Typography id={headingId} component="h2" variant="h6" sx={{ m: 0 }}><Trans>{title}</Trans></Typography>
            <Chip size="small" label={notifications.length} />
        </Stack>
        {past && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <Trans>Past requests and recent accepted or rejected bookings</Trans>
        </Typography>}
        <Stack ref={listRef} id={past ? 'past-notifications-list' : undefined} spacing={1.5}>
            {visible.map(notification => <React.Fragment key={notification.id}>
                {renderNotification(notification, past)}
            </React.Fragment>)}
            {!notifications.length && <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography color="text.secondary"><Trans>{past ? 'No past notifications' : 'No active notifications'}</Trans></Typography>
            </Paper>}
        </Stack>
        {past && !!notifications.length && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
            alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
            <Typography id="past-notifications-count" role="status" variant="body2" color="text.secondary">
                <Trans i18nKey="Showing {{shown}} of {{total}}" values={{ shown: visible.length, total: notifications.length }} />
            </Typography>
            {visible.length < notifications.length && <Button variant="outlined" sx={{ minHeight: 44, minWidth: 160 }}
                aria-controls="past-notifications-list" aria-describedby="past-notifications-count"
                onClick={event => {
                    if (event.detail > 1) return
                    setVisibleCount(count => Math.min(count + PAST_PAGE_SIZE, notifications.length))
                    if (event.detail === 0 && visible.length + PAST_PAGE_SIZE >= notifications.length) {
                        requestAnimationFrame(() => listRef.current?.children[visible.length]?.focus())
                    }
                }}>
                <Trans>Show more</Trans>
            </Button>}
        </Stack>}
    </Box>
}

export const NotificationsList = () => {
    const { active, past } = useNotifications()
    const siteId = useSelector(state => state.ui?.applyAdminSiteId
        || Object.keys(state.auth?.user?.specialPermissionsInSites || {})[0])
    const loaded = useSelector(state => !!state.site_admin?.pending_bookings && !!state.site_admin?.users)
    if (!loaded) return <Box role="status" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={28} /><Typography><Trans>Loading notifications…</Trans></Typography>
    </Box>
    return <>
        <NotificationSection notifications={active} />
        <NotificationSection key={siteId} notifications={past} past />
    </>
}

const AutoPermissionRequester = ({ user, enabled, onChangeEnabled }) => {
    useEffect(() => {
        console.log('AutoPermissionRequester - useEffect', { user, enabled, opener: window.opener })
        if (enabled === null || enabled === false) {
            onChangeEnabled(true)
        }
    }, [enabled, onChangeEnabled])
    return null
}

export class __NotificationSettings extends React.Component {
    render() {
        const { supported, enabled, preferences } = this.props.notifications
        console.log('__NotificationSettings', { supported, enabled, preferences })
        const { onChangeEnabled, user, autoRequestPermission, embedded } = this.props
        if (!supported) {
            return <NotificationsNotSupported />
        }

        return <div>
            <SwitchInput
                active={enabled}
                label="Notifications"
                onChange={value => onChangeEnabled(value)}
            />
            {autoRequestPermission && user &&
                <AutoPermissionRequester
                    user={user}
                    enabled={enabled}
                    onChangeEnabled={onChangeEnabled}
                />
            }
            {embedded && <p style={{ fontSize: '0.9em', marginTop: 16 }}>
                <Trans>Notifications don't work?</Trans>{' '}
                <a
                    href="/settings?requestNotificationPermission=true"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Trans>Try enabling them in BookingGood</Trans>
                </a>
            </p>}
        </div>
    }
}
export const NotificationSettings = connect(
    state => {
        const { notifications } = state.user_profile
        const { user } = state.auth || {}
        return {
            notifications,
            user,
        }
    },
    dispatch => {
        return {
            onChangeEnabled: enabled => dispatch(enabled ? notificationsEnabled() : notificationsDisabled())
        }
    }
)(__NotificationSettings)
